/**
 * Workflow Context - Single Source of Truth for Workflow State
 *
 * This context provides centralized state management for the workflow builder.
 * All components should consume workflow state (nodes, edges) from this context
 * rather than maintaining their own copies.
 *
 * Usage:
 *   1. Wrap your component tree with <WorkflowProvider>
 *   2. Use useWorkflow() hook to access state and dispatch actions
 *
 * Example:
 *   const { nodes, edges, dispatch } = useWorkflow();
 *   dispatch({ type: WORKFLOW_ACTIONS.SET_NODES, payload: newNodes });
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import { applyNodeChanges, applyEdgeChanges } from '@xyflow/react';
import {
  NODE_CATEGORIES,
  NODE_TYPES,
} from '../../../../constants/workflow.constant';

export const WORKFLOW_ACTIONS = {
  SET_NODES: 'SET_NODES',
  SET_EDGES: 'SET_EDGES',
  SET_NODES_FN: 'SET_NODES_FN',
  SET_EDGES_FN: 'SET_EDGES_FN',
  LOAD_WORKFLOW: 'LOAD_WORKFLOW',
  RESET_WORKFLOW: 'RESET_WORKFLOW',
  REMOVE_NODES_CASCADE: 'REMOVE_NODES_CASCADE',

  SET_OPEN_STAGE_FORM_MODAL: 'SET_OPEN_STAGE_FORM_MODAL',
  CLOSE_STAGE_FORM_MODAL: 'CLOSE_STAGE_FORM_MODAL',
  SET_LAST_NODE_ID: 'SET_LAST_NODE_ID',
};

export const createDefaultStartNode = () => ({
  id: 'start',
  type: NODE_TYPES.INPUT,
  category: NODE_CATEGORIES.START,
  position: { x: 250, y: 50 },
  data: { name: 'Start' },
});

// Older saves (before getWorkflowJsonData started stripping it) persisted a
// live Typography element into node.data.label. JSON round-tripping an
// element strips its $$typeof Symbol, leaving a plain object shaped like
// {type, key, ref, props, _owner} that WorkflowBuilder's displayNodes memo
// then tries to render as-is, crashing with "Objects are not valid as a
// React child" (React error #31). label is always re-derived from data.name
// for display, so it's safe to drop on load regardless of its shape.
const sanitizeLoadedNodes = (nodes) =>
  Array.isArray(nodes)
    ? nodes.map((node) => {
        if (!node?.data || !('label' in node.data)) {
          return node;
        }
        const { label, ...dataWithoutLabel } = node.data;
        return { ...node, data: dataWithoutLabel };
      })
    : nodes;

export const initialState = {
  nodes: [createDefaultStartNode()],
  edges: [],
  lastNodeId: null,
  openStageFormModal: {
    open: false,
    nodeId: null,
    nodeType: null,
    formConfig: null,
    nodeName: null,
    isNewNode: false,
  },
};

function collectCascadeIds(removeIds, nodes, edges) {
  const allIds = new Set(removeIds);

  for (const id of removeIds) {
    const node = nodes.find((n) => n.id === id);

    if (node?.data?.formConfig?.approver_role_id) {
      edges
        .filter((e) => e.source === id)
        .forEach((e) => {
          const target = nodes.find((n) => n.id === e.target);
          if (target?.scope === 'ReviewChildNode') allIds.add(e.target);
        });
    } else if (node?.category === NODE_CATEGORIES.CHILD_STA) {
      const childIds = edges
        .filter((e) => e.source === id)
        .map((e) => e.target);
      childIds.forEach((cid) => allIds.add(cid));
      edges
        .filter((e) => childIds.includes(e.source))
        .forEach((e) => allIds.add(e.target));
    }
  }

  return allIds;
}

/**
 * Workflow reducer - handles all state updates atomically
 */
export function workflowReducer(state, action) {
  switch (action.type) {
    case WORKFLOW_ACTIONS.SET_NODES:
      return { ...state, nodes: action.payload };

    case WORKFLOW_ACTIONS.SET_EDGES:
      return { ...state, edges: action.payload };

    case WORKFLOW_ACTIONS.SET_NODES_FN:
      return { ...state, nodes: action.payload(state.nodes) };

    case WORKFLOW_ACTIONS.SET_EDGES_FN:
      return { ...state, edges: action.payload(state.edges) };

    case WORKFLOW_ACTIONS.REMOVE_NODES_CASCADE: {
      const allIds = collectCascadeIds(
        action.payload,
        state.nodes,
        state.edges
      );
      return {
        ...state,
        nodes: state.nodes.filter((n) => !allIds.has(n.id)),
        edges: state.edges.filter(
          (e) => !allIds.has(e.source) && !allIds.has(e.target)
        ),
      };
    }

    case WORKFLOW_ACTIONS.LOAD_WORKFLOW:
      return {
        ...state,
        nodes: sanitizeLoadedNodes(action.payload.nodes) || [
          createDefaultStartNode(),
        ],
        edges: action.payload.edges || [],
        lastNodeId: null,
        openStageFormModal: initialState.openStageFormModal,
      };

    case WORKFLOW_ACTIONS.RESET_WORKFLOW:
      return {
        ...state,
        nodes: [createDefaultStartNode()],
        edges: [],
        lastNodeId: null,
        openStageFormModal: initialState.openStageFormModal,
      };

    case WORKFLOW_ACTIONS.SET_OPEN_STAGE_FORM_MODAL:
      return {
        ...state,
        openStageFormModal: action.payload,
      };

    case WORKFLOW_ACTIONS.CLOSE_STAGE_FORM_MODAL:
      return {
        ...state,
        openStageFormModal: initialState.openStageFormModal,
      };

    case WORKFLOW_ACTIONS.SET_LAST_NODE_ID:
      return {
        ...state,
        lastNodeId: action.payload,
      };

    default:
      return state;
  }
}

// Create context
const WorkflowContext = createContext(null);

/**
 * WorkflowProvider - Provides workflow state to component tree
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.initialWorkflow - Optional initial workflow data { nodes, edges }
 */
export function WorkflowProvider({ children, initialWorkflow = null }) {
  const [state, dispatch] = useReducer(
    workflowReducer,
    initialState,
    (initial) => {
      if (initialWorkflow?.nodes || initialWorkflow?.edges) {
        return {
          ...initial,
          nodes: sanitizeLoadedNodes(initialWorkflow.nodes) || [
            createDefaultStartNode(),
          ],
          edges: initialWorkflow.edges || [],
        };
      }
      return initial;
    }
  );

  const loadWorkflow = useCallback(
    (workflowData) => {
      const parsed =
        typeof workflowData === 'string'
          ? JSON.parse(workflowData)
          : workflowData;
      dispatch({ type: WORKFLOW_ACTIONS.LOAD_WORKFLOW, payload: parsed });
    },
    [dispatch]
  );

  const setNodes = useCallback(
    (nodesOrFn) => {
      if (typeof nodesOrFn === 'function') {
        dispatch({ type: WORKFLOW_ACTIONS.SET_NODES_FN, payload: nodesOrFn });
      } else {
        dispatch({ type: WORKFLOW_ACTIONS.SET_NODES, payload: nodesOrFn });
      }
    },
    [dispatch]
  );

  const setEdges = useCallback(
    (edgesOrFn) => {
      if (typeof edgesOrFn === 'function') {
        dispatch({ type: WORKFLOW_ACTIONS.SET_EDGES_FN, payload: edgesOrFn });
      } else {
        dispatch({ type: WORKFLOW_ACTIONS.SET_EDGES, payload: edgesOrFn });
      }
    },
    [dispatch]
  );

  const onNodesChange = useCallback(
    (changes) => {
      const removeChanges = changes.filter((c) => c.type === 'remove');
      const otherChanges = changes.filter((c) => c.type !== 'remove');

      if (otherChanges.length > 0) {
        dispatch({
          type: WORKFLOW_ACTIONS.SET_NODES_FN,
          payload: (currentNodes) =>
            applyNodeChanges(otherChanges, currentNodes),
        });
      }

      if (removeChanges.length > 0) {
        dispatch({
          type: WORKFLOW_ACTIONS.REMOVE_NODES_CASCADE,
          payload: removeChanges.map((c) => c.id),
        });
      }
    },
    [dispatch]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      dispatch({
        type: WORKFLOW_ACTIONS.SET_EDGES_FN,
        payload: (currentEdges) => applyEdgeChanges(changes, currentEdges),
      });
    },
    [dispatch]
  );

  const contextValue = useMemo(
    () => ({
      nodes: state.nodes,
      edges: state.edges,
      lastNodeId: state.lastNodeId,
      openStageFormModal: state.openStageFormModal,

      dispatch,
      loadWorkflow,
      setNodes,
      setEdges,
      onNodesChange,
      onEdgesChange,

      getWorkflowJson: () => ({
        nodes: state.nodes,
        edges: state.edges,
      }),
    }),
    [state, loadWorkflow, setNodes, setEdges, onNodesChange, onEdgesChange]
  );

  return (
    <WorkflowContext.Provider value={contextValue}>
      {children}
    </WorkflowContext.Provider>
  );
}

/**
 * useWorkflow - Hook to access workflow state and actions
 *
 * @returns {Object} Workflow context value
 * @throws {Error} If used outside of WorkflowProvider
 */
export function useWorkflow() {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
}

export default WorkflowContext;
